import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, useCallback } from "react";
import { useAnnouncer } from "@/components/ui/live-region";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { completeTourRun, fetchActiveRunForCurrentUser, type ActiveRun } from "@/lib/checkpoint-scans";
import { toast } from "sonner";
import { ScanLine, CheckCircle2, XCircle, Clock, Route as RouteIcon, Info } from "lucide-react";
import {
  describeSchedule, effectiveEnforcement, enforcementGuardCopy, isScheduleActiveAt,
  type TourScheduleEnforcement,
} from "@/lib/tour-schedule";

export const Route = createFileRoute("/_authenticated/guard-tours/active")({
  component: ActivePatrolPage,
});

type CheckpointRow = {
  id: string;
  sequence: number;
  name: string;
  scanned_at: string | null;
  instructions: string | null;
  scan_mode: "tour" | "occurrences" | "interval";
  required_occurrences: number;
  scan_interval_minutes: number | null;
  reference_photos: { name: string; url: string; description?: string | null }[];
  scan_count: number;
  enforcement: TourScheduleEnforcement;
};

type TourContext = {
  tour_type: string | null;
  schedule_days: number[] | null;
  schedule_start_time: string | null;
  schedule_end_time: string | null;
  schedule_enforcement: string | null;
  advisory_note: string | null;
};

function formatElapsed(startIso: string) {
  const ms = Date.now() - new Date(startIso).getTime();
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${m}m`;
  return `${m}m ${sec}s`;
}

function ActivePatrolPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState<ActiveRun | null>(null);
  const [checkpoints, setCheckpoints] = useState<CheckpointRow[]>([]);
  const [tourCtx, setTourCtx] = useState<TourContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [ending, setEnding] = useState(false);
  const [, force] = useState(0);
  const { announce, region } = useAnnouncer();
  const announcedRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const a = await fetchActiveRunForCurrentUser();
    setActive(a);
    if (a) {
      const { data: tourRow } = await supabase
        .from("guard_tours")
        .select("tour_type,schedule_days,schedule_start_time,schedule_end_time,schedule_enforcement,advisory_note")
        .eq("id", a.tour_id)
        .maybeSingle();
      setTourCtx((tourRow ?? null) as TourContext | null);
      const tourEnforcement = (tourRow as any)?.schedule_enforcement ?? "strict";
      const { data: cps } = await supabase
        .from("tour_checkpoints")
        .select("id,sequence,name,instructions,scan_mode,required_occurrences,scan_interval_minutes,schedule_enforcement,reference_photos")
        .eq("tour_id", a.tour_id)
        .order("sequence");
      const { data: scans } = await supabase
        .from("checkpoint_scans")
        .select("checkpoint_id,scanned_at")
        .eq("tour_run_id", a.id)
        .order("scanned_at");
      const lastScan: Record<string, string> = {};
      const scanCounts: Record<string, number> = {};
      for (const s of scans ?? []) {
        lastScan[(s as any).checkpoint_id] = (s as any).scanned_at;
        scanCounts[(s as any).checkpoint_id] = (scanCounts[(s as any).checkpoint_id] ?? 0) + 1;
      }
      setCheckpoints(
        (cps ?? []).map((c: any) => ({
          id: c.id, sequence: c.sequence, name: c.name,
          instructions: c.instructions ?? null,
          scan_mode: (c.scan_mode ?? "tour") as CheckpointRow["scan_mode"],
          required_occurrences: c.required_occurrences ?? 1,
          scan_interval_minutes: c.scan_interval_minutes ?? null,
          reference_photos: Array.isArray(c.reference_photos) ? c.reference_photos : [],
          enforcement: effectiveEnforcement(tourEnforcement, c.schedule_enforcement ?? null),
          scan_count: scanCounts[c.id] ?? 0,
          scanned_at: lastScan[c.id] ?? null,
        })),
      );
    } else {
      setCheckpoints([]);
      setTourCtx(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // tick clock + realtime scan updates
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!active) return;
    const ch = supabase
      .channel(`active-run-${active.id}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "checkpoint_scans", filter: `tour_run_id=eq.${active.id}` },
        () => load(),
      )
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [active?.id, load]);

  // Scans arrive over realtime, so the list changes without any user action.
  // Announce the new coverage so it is not a silent visual update.
  useEffect(() => {
    if (!active || checkpoints.length === 0) return;
    const done = checkpoints.filter((c) => c.scanned_at).length;
    const key = `${active.id}:${done}`;
    if (announcedRef.current === key) return;
    const first = announcedRef.current === null;
    announcedRef.current = key;
    if (first) return; // the page heading already conveys the starting state
    announce(`Checkpoint scan recorded. ${done} of ${checkpoints.length} checkpoints complete.`);
  }, [active, announce, checkpoints]);

  const endRun = async (abandoned: boolean) => {
    if (!active) return;
    setEnding(true);
    announce(abandoned ? "Abandoning this patrol." : "Completing this patrol.");
    try {
      await completeTourRun(active.id, abandoned);
      toast.success(abandoned ? "Patrol abandoned" : "Patrol completed");
      announce(
        abandoned
          ? "Patrol abandoned. Opening the patrol record."
          : "Patrol completed. Opening the patrol record.",
      );
      navigate({ to: "/guard-tours/runs/$runId", params: { runId: active.id } });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to end patrol");
      announce(e?.message ?? "Failed to end patrol.");
    } finally {
      setEnding(false);
    }
  };

  if (loading) {
    return (
      <div role="status" className="p-8 text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!active) {
    return (
      <div className="mx-auto max-w-xl px-4 py-12 text-center">
        <RouteIcon className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-2xl font-semibold tracking-tight">No active patrol</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          A patrol starts automatically the first time you scan a checkpoint QR.
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button asChild><Link to="/guard-tours/scan"><ScanLine className="h-4 w-4" /> Open scanner</Link></Button>
          <Button asChild variant="outline"><Link to="/guard-tours">All tours</Link></Button>
        </div>
      </div>
    );
  }

  const completed = checkpoints.filter((c) => c.scanned_at).length;
  const total = checkpoints.length;
  const remaining = total - completed;
  const percent = total === 0 ? 0 : Math.round((completed / total) * 100);
  const flexibleTour = tourCtx?.schedule_enforcement === "flexible";

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-8">
      {region}
      <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-emerald-500" aria-hidden="true" />
        Active patrol
      </div>
      <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">{active.tour_name}</h1>
      {/* role="timer" keeps the ticking clock readable on demand without
          announcing every second over real status changes. */}
      <div role="timer" aria-label="Time elapsed on this patrol" className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
        <Clock className="h-4 w-4" aria-hidden="true" /> {formatElapsed(active.started_at)} elapsed
      </div>

      {tourCtx && (
        <Card className="mt-4 space-y-2 border-primary/30 bg-primary/5 p-4 text-sm">
          <div className="flex items-start gap-2">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <span>{enforcementGuardCopy(tourCtx.schedule_enforcement === "flexible" ? "flexible" : "strict")}</span>
          </div>
          {tourCtx.tour_type === "on_site" && (
            <div className="text-xs text-muted-foreground">
              {describeSchedule(tourCtx.schedule_days, tourCtx.schedule_start_time, tourCtx.schedule_end_time)}
              {isScheduleActiveAt(tourCtx.schedule_days, tourCtx.schedule_start_time, tourCtx.schedule_end_time)
                ? " · in your scheduled window"
                : " · outside the scheduled window"}
            </div>
          )}
          {tourCtx.advisory_note && (
            <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">{tourCtx.advisory_note}</p>
          )}
        </Card>
      )}

      <Card className="mt-5 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{completed} of {total} checkpoints</span>
          <span className="text-muted-foreground">{percent}%</span>
        </div>
        <Progress
          value={percent}
          className="mt-2 h-2"
          aria-label="Checkpoint progress"
          aria-valuetext={`${completed} of ${total} checkpoints scanned, ${percent} percent`}
        />
        <div className="mt-2 text-xs text-muted-foreground">
          {flexibleTour
            ? `${completed} of ${total} logged — coverage is recorded as-is on this flexible tour.`
            : remaining > 0
              ? `${remaining} remaining`
              : "All checkpoints scanned — finish to log this patrol."}
        </div>
      </Card>

      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Button asChild className="col-span-2 sm:col-span-1">
          <Link to="/guard-tours/scan"><ScanLine className="h-4 w-4" /> Scan next</Link>
        </Button>
        <Button variant="outline" disabled={ending} aria-busy={ending} onClick={() => endRun(false)}>
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> End patrol
        </Button>
        <Button variant="outline" disabled={ending} aria-busy={ending} onClick={() => endRun(true)}>
          <XCircle className="h-4 w-4" aria-hidden="true" /> Abandon
        </Button>
      </div>

      <h2 id="checkpoints-heading" className="mt-8 mb-3 text-sm font-medium text-muted-foreground">Checkpoints</h2>
      <Card className="divide-y" role="list" aria-labelledby="checkpoints-heading">
        {checkpoints.map((c) => {
          const needed = c.scan_mode === "occurrences" ? Math.max(1, c.required_occurrences) : 1;
          const done = c.scan_mode === "occurrences" ? c.scan_count >= needed : !!c.scanned_at;
          return (
            <div key={c.id} role="listitem" className="flex items-start gap-3 p-3 text-sm">
              <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                done ? "bg-emerald-500/15 text-emerald-500" : "bg-muted text-muted-foreground"
              }`}>
                {done ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : c.sequence}
                {/* Completion is otherwise conveyed by colour and icon alone. */}
                <span className="sr-only">
                  {done ? `Checkpoint ${c.sequence}, completed.` : `Checkpoint ${c.sequence}, not yet scanned.`}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="min-w-0 truncate font-medium">{c.name}</span>
                  {c.scan_mode === "occurrences" && (
                    <Badge variant="secondary" className="text-[10px]">{c.scan_count}/{needed} scans</Badge>
                  )}
                  {c.scan_mode === "interval" && c.scan_interval_minutes && (
                    <Badge variant="secondary" className="text-[10px]">every {c.scan_interval_minutes}m</Badge>
                  )}
                </div>
                {c.instructions && (
                  <p className="mt-0.5 whitespace-pre-wrap break-words text-xs text-muted-foreground">{c.instructions}</p>
                )}
                {c.reference_photos.length > 0 && (
                  <div className="mt-2 flex gap-2 overflow-x-auto">
                    {c.reference_photos.map((p, i) => (
                      <figure key={i} className="w-20 shrink-0">
                        <a href={p.url} target="_blank" rel="noreferrer">
                          <img src={p.url} alt={p.description || p.name} loading="lazy"
                               className="aspect-square w-20 rounded border border-border/60 object-cover" />
                        </a>
                        {p.description && (
                          <figcaption className="mt-0.5 break-words text-[10px] leading-snug text-muted-foreground">
                            {p.description}
                          </figcaption>
                        )}
                      </figure>
                    ))}
                  </div>
                )}
                {c.scanned_at && (
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Last scanned {new Date(c.scanned_at).toLocaleTimeString()}
                  </div>
                )}
              </div>
              {!done && (
                <Badge variant="outline" className="shrink-0 text-muted-foreground">
                  {c.enforcement === "flexible" ? "optional" : "pending"}
                </Badge>
              )}
            </div>
          );
        })}
      </Card>
    </div>
  );
}
